import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Home, Users, UserPlus, Copy, Check, Shield, Trash2, Loader2, LogOut, Swords, Activity, Trophy, Scale, Apple, Camera, Dumbbell, Target } from 'lucide-react';
import { API } from '../App';
import HamburgerMenu from '../components/HamburgerMenu';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Input } from '../components/ui/input';

const PRIVACY_OPTIONS = [
  ['share_workout_completion', 'Workout completion', 'Share whether you completed a workout.'],
  ['share_workout_details', 'Workout details', 'Share exercises, sets, reps, and workout details.'],
  ['share_progress', 'Progress trends', 'Share workout and performance progress trends.'],
  ['share_body_weight', 'Body weight', 'Share logged body-weight information.'],
  ['share_nutrition', 'Nutrition', 'Share nutrition and macro information.'],
  ['share_progress_photos', 'Progress photos', 'Share progress photos with the Family Owner.'],
];

const FamilyHub = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [familyData, setFamilyData] = useState(null);
  const [members, setMembers] = useState([]);
  const [dashboardMembers, setDashboardMembers] = useState([]);
  const [familyChallenges, setFamilyChallenges] = useState([]);
  const [familyRecommendations, setFamilyRecommendations] = useState([]);
  const [familyGoals, setFamilyGoals] = useState([]);
  const [goalTarget, setGoalTarget] = useState(null);
  const [goalTitle, setGoalTitle] = useState('');
  const [goalWeeklyTarget, setGoalWeeklyTarget] = useState(4);
  const [recommendationTarget, setRecommendationTarget] = useState(null);
  const [recommendationTitle, setRecommendationTitle] = useState('');
  const [recommendationWorkoutType, setRecommendationWorkoutType] = useState('home');
  const [recommendationFocus, setRecommendationFocus] = useState('full_body');
  const [recommendationDate, setRecommendationDate] = useState('');
  const [recommendationNotes, setRecommendationNotes] = useState('');
  const [privacy, setPrivacy] = useState({});
  const [familyName, setFamilyName] = useState('');
  const [joinCode, setJoinCode] = useState('');
  const [inviteEmail, setInviteEmail] = useState('');
  const [latestInvite, setLatestInvite] = useState(null);
  const [copied, setCopied] = useState(false);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const mine = await axios.get(`${API}/family/mine`);
      setFamilyData(mine.data);
      if (mine.data?.family?.id) {
        const familyId = mine.data.family.id;
        const [
          membersRes,
          privacyRes,
          dashboardRes,
          pendingRes,
          outgoingRes,
          activeRes,
        ] = await Promise.all([
          axios.get(`${API}/family/${familyId}/members`),
          axios.get(`${API}/family/${familyId}/privacy`),
          axios.get(`${API}/family/${familyId}/dashboard`),
          axios.get(`${API}/battles/pending`),
          axios.get(`${API}/battles/outgoing`),
          axios.get(`${API}/battles/active`),
        ]);

        setMembers(membersRes.data.members || []);
        setDashboardMembers(dashboardRes.data.members || []);
        setPrivacy(privacyRes.data || {});

        try {
          const recommendationsRes = await axios.get(
            `${API}/family/${familyId}/workout-recommendations`
          );
          setFamilyRecommendations(
            recommendationsRes.data.recommendations || []
          );
        } catch (recommendationError) {
          console.error(
            'Could not load Family workout recommendations:',
            recommendationError
          );
          setFamilyRecommendations([]);
        }

        try {
          const goalsRes = await axios.get(
            `${API}/family/${familyId}/goals`
          );
          setFamilyGoals(goalsRes.data.goals || []);
        } catch (goalError) {
          console.error(
            'Could not load Family goals:',
            goalError
          );
          setFamilyGoals([]);
        }

        const allBattles = [
          ...(pendingRes.data.pending_battles || []),
          ...(outgoingRes.data.outgoing_battles || []),
          ...(activeRes.data.active_battles || []),
        ];

        const familyBattleMap = new Map();
        allBattles
          .filter(
            (battle) =>
              battle.relationship_source === 'family' &&
              battle.family_id === familyId &&
              ['pending', 'active'].includes(battle.status)
          )
          .forEach((battle) => familyBattleMap.set(battle.id, battle));

        const statusRank = { active: 0, pending: 1 };
        setFamilyChallenges(
          Array.from(familyBattleMap.values()).sort(
            (a, b) =>
              (statusRank[a.status] ?? 9) - (statusRank[b.status] ?? 9)
          )
        );
      } else {
        setMembers([]);
        setDashboardMembers([]);
        setFamilyChallenges([]);
        setFamilyRecommendations([]);
        setFamilyGoals([]);
        setGoalTarget(null);
        setRecommendationTarget(null);
        setPrivacy({});
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Could not load Family account.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const createFamily = async (e) => {
    e.preventDefault();
    if (!familyName.trim()) return;
    setBusy(true);
    try {
      await axios.post(`${API}/family`, { name: familyName.trim() });
      toast.success('Family account created.');
      setFamilyName('');
      await load();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Could not create Family account.');
    } finally { setBusy(false); }
  };

  const joinFamily = async (e) => {
    e.preventDefault();
    if (!joinCode.trim()) return;
    setBusy(true);
    try {
      const response = await axios.post(`${API}/family/join`, { invite_code: joinCode.trim() });
      toast.success(`Joined ${response.data.family.name}.`);
      setJoinCode('');
      await load();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Could not join Family account.');
    } finally { setBusy(false); }
  };

  const createInvite = async (e) => {
    e.preventDefault();
    const familyId = familyData?.family?.id;
    if (!familyId) return;
    setBusy(true);
    try {
      const response = await axios.post(`${API}/family/${familyId}/invites`, { email: inviteEmail.trim() || null });
      setLatestInvite(response.data);
      setInviteEmail('');
      toast.success('Family invite created.');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Could not create Family invite.');
    } finally { setBusy(false); }
  };

  const copyInvite = async () => {
    if (!latestInvite?.invite_code) return;
    await navigator.clipboard.writeText(latestInvite.invite_code);
    setCopied(true);
    toast.success('Invite code copied.');
    setTimeout(() => setCopied(false), 1500);
  };

  const savePrivacy = async () => {
    const familyId = familyData?.family?.id;
    if (!familyId) return;
    setBusy(true);
    try {
      await axios.put(`${API}/family/${familyId}/privacy`, privacy);
      toast.success('Family sharing preferences saved.');
      await load();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Could not save sharing preferences.');
    } finally { setBusy(false); }
  };

  const removeMember = async (userId) => {
    const familyId = familyData?.family?.id;
    if (!familyId) return;
    setBusy(true);
    try {
      await axios.delete(`${API}/family/${familyId}/members/${userId}`);
      toast.success('Family member removed.');
      await load();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Could not remove Family member.');
    } finally { setBusy(false); }
  };

  const beginFamilyGoal = (member) => {
    if (!member?.user_id) return;

    const existing = familyGoals.find(
      (goal) => goal.member_user_id === member.user_id
    );

    setGoalTarget(member);
    setGoalTitle(existing?.title || 'Weekly Workout Goal');
    setGoalWeeklyTarget(existing?.weekly_workout_target || 4);
  };

  const cancelFamilyGoal = () => {
    setGoalTarget(null);
    setGoalTitle('');
    setGoalWeeklyTarget(4);
  };

  const saveFamilyGoal = async (e) => {
    e.preventDefault();

    const familyId = familyData?.family?.id;

    if (!familyId || !goalTarget?.user_id) return;

    const target = Number(goalWeeklyTarget);

    if (!Number.isInteger(target) || target < 1 || target > 7) {
      toast.error('Weekly workout target must be between 1 and 7.');
      return;
    }

    setBusy(true);

    try {
      await axios.put(
        `${API}/family/${familyId}/goals/${goalTarget.user_id}`,
        {
          weekly_workout_target: target,
          title: goalTitle.trim() || 'Weekly Workout Goal',
        }
      );

      toast.success(
        goalTarget.user_id === membership?.user_id
          ? 'Your Family goal was saved.'
          : `Goal saved for ${goalTarget.username}.`
      );

      cancelFamilyGoal();
      await load();
    } catch (error) {
      toast.error(
        error.response?.data?.detail ||
          'Could not save this Family goal.'
      );
    } finally {
      setBusy(false);
    }
  };

  const deleteFamilyGoal = async (memberUserId) => {
    const familyId = familyData?.family?.id;

    if (!familyId || !memberUserId) return;

    setBusy(true);

    try {
      await axios.delete(
        `${API}/family/${familyId}/goals/${memberUserId}`
      );

      toast.success('Family goal removed.');

      if (goalTarget?.user_id === memberUserId) {
        cancelFamilyGoal();
      }

      await load();
    } catch (error) {
      toast.error(
        error.response?.data?.detail ||
          'Could not remove this Family goal.'
      );
    } finally {
      setBusy(false);
    }
  };

  const beginWorkoutRecommendation = (member) => {
    setRecommendationTarget(member);
    setRecommendationTitle('');
    setRecommendationWorkoutType('home');
    setRecommendationFocus('full_body');
    setRecommendationDate('');
    setRecommendationNotes('');
  };

  const cancelWorkoutRecommendation = () => {
    setRecommendationTarget(null);
    setRecommendationTitle('');
    setRecommendationWorkoutType('home');
    setRecommendationFocus('full_body');
    setRecommendationDate('');
    setRecommendationNotes('');
  };

  const submitWorkoutRecommendation = async (e) => {
    e.preventDefault();

    const familyId = familyData?.family?.id;

    if (!familyId || !recommendationTarget?.user_id) return;

    if (!recommendationTitle.trim()) {
      toast.error('Give the workout recommendation a title.');
      return;
    }

    if (!recommendationFocus.trim()) {
      toast.error('Enter a workout focus.');
      return;
    }

    setBusy(true);

    try {
      await axios.post(
        `${API}/family/${familyId}/workout-recommendations`,
        {
          member_user_id: recommendationTarget.user_id,
          title: recommendationTitle.trim(),
          workout_type: recommendationWorkoutType,
          focus: recommendationFocus.trim(),
          scheduled_date: recommendationDate || null,
          notes: recommendationNotes.trim() || null,
        }
      );

      toast.success(
        `Workout recommended to ${recommendationTarget.username}.`
      );

      cancelWorkoutRecommendation();
      await load();
    } catch (error) {
      toast.error(
        error.response?.data?.detail ||
          'Could not recommend this workout.'
      );
    } finally {
      setBusy(false);
    }
  };

  const respondToWorkoutRecommendation = async (
    recommendationId,
    action
  ) => {
    const familyId = familyData?.family?.id;

    if (!familyId) return;

    setBusy(true);

    try {
      await axios.post(
        `${API}/family/${familyId}/workout-recommendations/${recommendationId}/${action}`
      );

      toast.success(
        action === 'accept'
          ? 'Workout recommendation accepted.'
          : 'Workout recommendation dismissed.'
      );

      await load();
    } catch (error) {
      toast.error(
        error.response?.data?.detail ||
          'Could not update this recommendation.'
      );
    } finally {
      setBusy(false);
    }
  };

  const startWorkoutRecommendation = (recommendation) => {
    if (
      recommendation?.member_user_id !==
        familyData?.membership?.user_id ||
      recommendation?.status !== 'accepted'
    ) {
      toast.error(
        'Only your accepted recommendation can be started.'
      );
      return;
    }

    navigate('/log-workout', {
      state: {
        familyRecommendation: recommendation,
      },
    });
  };

  const leaveFamily = async () => {
    const familyId = familyData?.family?.id;
    if (!familyId) return;
    setBusy(true);
    try {
      await axios.post(`${API}/family/${familyId}/leave`);
      toast.success('You left the Family account.');
      await load();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Could not leave Family account.');
    } finally { setBusy(false); }
  };

  if (loading) {
    return <div className="min-h-screen bg-gradient-to-br from-stone-950 via-black to-stone-900"><HamburgerMenu /><div className="flex justify-center py-28"><Loader2 className="w-8 h-8 text-blood-400 animate-spin" /></div></div>;
  }

  const family = familyData?.family;
  const membership = familyData?.membership;
  const isOwner = membership?.role === 'family_owner';

  const familyGroupCandidates = members
    .filter(
      (member) =>
        member.user_id !== membership?.user_id
    )
    .map((member) => ({
      user_id: member.user_id,
      username: member.username,
    }));

  return (
    <div className="min-h-screen bg-gradient-to-br from-stone-950 via-black to-stone-900 pb-16" data-testid="family-hub-page">
      <HamburgerMenu />
      <div className="max-w-5xl mx-auto px-4 py-6">
        <div className="flex items-center gap-3 mb-6">
          <div className="w-12 h-12 rounded-lg bg-blood-950 border border-blood-800 flex items-center justify-center"><Home className="w-6 h-6 text-blood-400" /></div>
          <div><h1 className="text-2xl md:text-3xl text-white font-bold tracking-wider">FAMILY</h1><p className="text-stone-500 text-sm">Your household fitness account</p></div>
        </div>

        {!family ? (
          <div className="grid md:grid-cols-2 gap-5">
            <Card className="glass border-stone-800 p-6">
              <h2 className="text-lg text-white font-bold mb-2">Create a Family</h2>
              <p className="text-stone-400 text-sm mb-4">Create a household with up to 5 total members, including you.</p>
              <form onSubmit={createFamily} className="space-y-3">
                <Input value={familyName} onChange={(e) => setFamilyName(e.target.value)} placeholder="e.g. Flora Family" maxLength={80} className="bg-stone-900 border-stone-700 text-white" data-testid="family-create-name" />
                <Button disabled={busy} type="submit" className="w-full bg-blood-700 hover:bg-blood-800 text-white font-bold" data-testid="family-create-btn">Create Family</Button>
              </form>
            </Card>
            <Card className="glass border-stone-800 p-6">
              <h2 className="text-lg text-white font-bold mb-2">Join a Family</h2>
              <p className="text-stone-400 text-sm mb-4">Enter the invite code sent by your Family Owner.</p>
              <form onSubmit={joinFamily} className="space-y-3">
                <Input value={joinCode} onChange={(e) => setJoinCode(e.target.value.toUpperCase())} placeholder="Invite code" maxLength={12} className="bg-stone-900 border-stone-700 text-white uppercase tracking-widest" data-testid="family-join-code" />
                <Button disabled={busy} type="submit" variant="outline" className="w-full border-blood-800 text-blood-400 font-bold" data-testid="family-join-btn">Join Family</Button>
              </form>
            </Card>
          </div>
        ) : (
          <>
            <Card className="glass border-blood-900/70 p-6 mb-5">
              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                <div><p className="text-xs uppercase tracking-widest text-blood-400">{isOwner ? 'Family Owner' : 'Family Member'}</p><h2 className="text-2xl text-white font-bold">{family.name}</h2><p className="text-stone-400 text-sm mt-1">{familyData.member_count}/{familyData.max_members} household members</p></div>
                {!isOwner && <Button onClick={leaveFamily} disabled={busy} variant="outline" className="border-stone-700 text-stone-300"><LogOut className="w-4 h-4 mr-2" /> Leave Family</Button>}
              </div>
            </Card>

            {isOwner && (
              <Card className="glass border-stone-800 p-6 mb-5" data-testid="family-invite-card">
                <div className="flex items-center gap-2 mb-2"><UserPlus className="w-5 h-5 text-blood-400" /><h2 className="text-lg text-white font-bold">Invite Household Member</h2></div>
                <p className="text-stone-400 text-sm mb-4">Family accounts support up to 5 people total. Email is optional; every invite code is single-use.</p>
                <form onSubmit={createInvite} className="flex flex-col sm:flex-row gap-2">
                  <Input type="email" value={inviteEmail} onChange={(e) => setInviteEmail(e.target.value)} placeholder="Email (optional)" className="bg-stone-900 border-stone-700 text-white" />
                  <Button disabled={busy || familyData.member_count >= familyData.max_members} type="submit" className="bg-blood-700 hover:bg-blood-800 text-white font-bold">Create Invite</Button>
                </form>
                {latestInvite && <div className="mt-4 rounded-lg border border-stone-700 bg-stone-900/60 p-3 flex items-center justify-between"><div><p className="text-xs text-stone-500 uppercase tracking-wider">Invite Code</p><p className="text-white text-xl font-bold tracking-[0.2em]" data-testid="family-latest-invite-code">{latestInvite.invite_code}</p></div><Button onClick={copyInvite} variant="outline" className="border-stone-700 text-stone-300">{copied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}</Button></div>}
              </Card>
            )}

            <Card className="glass border-stone-800 p-6 mb-5" data-testid="family-household-dashboard">
              <div className="flex items-center gap-2 mb-1">
                <Users className="w-5 h-5 text-blood-400" />
                <h2 className="text-lg text-white font-bold">Household</h2>
              </div>
              <p className="text-stone-500 text-xs mb-4">
                {isOwner
                  ? "Household summaries respect each member's Family Sharing choices."
                  : 'Your own summary is visible to you. Other members control what they share with the Family Owner.'}
              </p>

              <div className="space-y-4">
                {members.map((member) => {
                  const summary = dashboardMembers.find(
                    (item) => item.user_id === member.user_id
                  );

                  const completion = summary?.workout_completion;
                  const details = summary?.workout_details;
                  const progress = summary?.progress;
                  const bodyWeight = summary?.body_weight;
                  const nutrition = summary?.nutrition;
                  const photos = summary?.progress_photos;

                  return (
                    <div
                      key={member.user_id}
                      className="rounded-lg border border-stone-800 bg-stone-900/50 p-4"
                    >
                      <div className="flex items-center justify-between gap-3">
                        <div>
                          <p className="text-white font-semibold">{member.username}</p>
                          <p className="text-stone-500 text-xs">
                            {member.role === 'family_owner' ? 'Family Owner' : 'Family Member'}
                          </p>
                        </div>

                        <div className="flex items-center gap-1">
                          {isOwner && (
                            <Button
                              type="button"
                              variant="outline"
                              className="border-stone-700 text-stone-300 hover:text-white"
                              onClick={() => beginFamilyGoal(member)}
                              data-testid={`family-set-goal-${member.user_id}`}
                            >
                              <Target className="w-4 h-4 mr-2" />
                              Goal
                            </Button>
                          )}

                          {isOwner && member.role === 'family_member' && (
                            <Button
                              type="button"
                              variant="outline"
                              className="border-stone-700 text-stone-300 hover:text-white"
                              onClick={() => beginWorkoutRecommendation(member)}
                              data-testid={`family-recommend-workout-${member.user_id}`}
                            >
                              <Dumbbell className="w-4 h-4 mr-2" />
                              Recommend
                            </Button>
                          )}

                          {member.user_id !== membership?.user_id && (
                            <Button
                              type="button"
                              variant="outline"
                              className="border-blood-800 text-blood-400 hover:text-blood-300"
                              onClick={() => navigate('/battle-arena', {
                                state: {
                                  challengeUsername: member.username,
                                  challengeSource: 'family',
                                },
                              })}
                              data-testid={`family-challenge-${member.user_id}`}
                            >
                              <Swords className="w-4 h-4 mr-2" />
                              Challenge
                            </Button>
                          )}

                          {isOwner && member.role === 'family_member' && (
                            <Button
                              onClick={() => removeMember(member.user_id)}
                              disabled={busy}
                              variant="ghost"
                              className="text-stone-500 hover:text-red-400"
                              title="Remove member"
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          )}
                        </div>
                      </div>

                      {summary && (
                        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-2 mt-4">
                          <div className="rounded-md border border-stone-800 bg-black/20 p-3">
                            <div className="flex items-center gap-2 text-stone-400 text-xs mb-1">
                              <Activity className="w-4 h-4" />
                              Workout Activity
                            </div>
                            {completion?.shared ? (
                              <>
                                <p className="text-white font-semibold">
                                  {completion.workouts_this_week ?? 0} this week
                                </p>
                                <p className="text-stone-500 text-xs">
                                  {completion.workouts_30d ?? 0} in 30 days
                                </p>
                              </>
                            ) : (
                              <p className="text-stone-600 text-sm">Private</p>
                            )}
                          </div>

                          <div className="rounded-md border border-stone-800 bg-black/20 p-3">
                            <div className="flex items-center gap-2 text-stone-400 text-xs mb-1">
                              <Trophy className="w-4 h-4" />
                              Progress
                            </div>
                            {progress?.shared ? (
                              progress.top_prs?.length ? (
                                <>
                                  <p className="text-white font-semibold truncate">
                                    {progress.top_prs[0].exercise_name}
                                  </p>
                                  <p className="text-stone-500 text-xs">
                                    {progress.top_prs[0].max_weight} max
                                  </p>
                                </>
                              ) : (
                                <p className="text-stone-500 text-sm">No PRs logged</p>
                              )
                            ) : (
                              <p className="text-stone-600 text-sm">Private</p>
                            )}
                          </div>

                          <div className="rounded-md border border-stone-800 bg-black/20 p-3">
                            <div className="flex items-center gap-2 text-stone-400 text-xs mb-1">
                              <Scale className="w-4 h-4" />
                              Body Weight
                            </div>
                            {bodyWeight?.shared ? (
                              bodyWeight.latest_weight != null ? (
                                <>
                                  <p className="text-white font-semibold">
                                    {bodyWeight.latest_weight} {bodyWeight.weight_unit}
                                  </p>
                                  <p className="text-stone-500 text-xs">
                                    {bodyWeight.change_30d == null
                                      ? 'No 30-day trend yet'
                                      : `${bodyWeight.change_30d > 0 ? '+' : ''}${bodyWeight.change_30d} ${bodyWeight.weight_unit} / 30d`}
                                  </p>
                                </>
                              ) : (
                                <p className="text-stone-500 text-sm">No weight logged</p>
                              )
                            ) : (
                              <p className="text-stone-600 text-sm">Private</p>
                            )}
                          </div>

                          <div className="rounded-md border border-stone-800 bg-black/20 p-3">
                            <div className="flex items-center gap-2 text-stone-400 text-xs mb-1">
                              <Apple className="w-4 h-4" />
                              Nutrition
                            </div>
                            {nutrition?.shared ? (
                              nutrition.days_logged ? (
                                <>
                                  <p className="text-white font-semibold">
                                    {nutrition.avg_calories} avg cal
                                  </p>
                                  <p className="text-stone-500 text-xs">
                                    {nutrition.avg_protein}g protein | {nutrition.days_logged} days
                                  </p>
                                </>
                              ) : (
                                <p className="text-stone-500 text-sm">No recent logs</p>
                              )
                            ) : (
                              <p className="text-stone-600 text-sm">Private</p>
                            )}
                          </div>

                          <div className="rounded-md border border-stone-800 bg-black/20 p-3">
                            <div className="flex items-center gap-2 text-stone-400 text-xs mb-1">
                              <Camera className="w-4 h-4" />
                              Progress Photos
                            </div>
                            {photos?.shared ? (
                              <p className="text-white font-semibold">
                                {photos.count ?? 0} shared
                              </p>
                            ) : (
                              <p className="text-stone-600 text-sm">Private</p>
                            )}
                          </div>

                          <div className="rounded-md border border-stone-800 bg-black/20 p-3">
                            <div className="flex items-center gap-2 text-stone-400 text-xs mb-1">
                              <Activity className="w-4 h-4" />
                              Recent Workout
                            </div>
                            {details?.shared ? (
                              details.recent_workouts?.length ? (
                                <>
                                  <p className="text-white font-semibold">
                                    {details.recent_workouts[0].date || 'Recent'}
                                  </p>
                                  <p className="text-stone-500 text-xs">
                                    {details.recent_workouts[0].exercises?.length || 0} exercises
                                  </p>
                                </>
                              ) : (
                                <p className="text-stone-500 text-sm">No recent workout</p>
                              )
                            ) : (
                              <p className="text-stone-600 text-sm">Private</p>
                            )}
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </Card>

            <Card
              className="glass border-stone-800 p-6 mb-5"
              data-testid="family-goals-card"
            >
              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-2">
                <div>
                  <div className="flex items-center gap-2">
                    <Target className="w-5 h-5 text-blood-400" />
                    <h2 className="text-lg text-white font-bold">
                      Family Goals
                    </h2>
                  </div>

                  <p className="text-stone-500 text-xs mt-1">
                    {isOwner
                      ? 'Set weekly workout goals for your household. Member progress is visible only when workout-completion sharing is enabled.'
                      : 'Set your own weekly workout goal. Your progress stays private from the Family Owner unless you share workout completion.'}
                  </p>
                </div>

                {!isOwner && (
                  <Button
                    type="button"
                    variant="outline"
                    className="border-blood-800 text-blood-400"
                    onClick={() =>
                      beginFamilyGoal(
                        members.find(
                          (member) =>
                            member.user_id === membership?.user_id
                        )
                      )
                    }
                    disabled={
                      busy ||
                      !members.some(
                        (member) =>
                          member.user_id === membership?.user_id
                      )
                    }
                    data-testid="family-set-my-goal"
                  >
                    <Target className="w-4 h-4 mr-2" />
                    Set My Goal
                  </Button>
                )}
              </div>

              {goalTarget && (
                <form
                  onSubmit={saveFamilyGoal}
                  className="rounded-lg border border-blood-900/60 bg-blood-950/20 p-4 mb-4 space-y-3"
                  data-testid="family-goal-form"
                >
                  <div className="flex items-center justify-between gap-3">
                    <div>
                      <p className="text-xs uppercase tracking-wider text-blood-400">
                        Weekly goal for
                      </p>
                      <p className="text-white font-semibold">
                        {goalTarget.username}
                      </p>
                    </div>

                    <Button
                      type="button"
                      variant="ghost"
                      className="text-stone-500"
                      onClick={cancelFamilyGoal}
                      disabled={busy}
                    >
                      Cancel
                    </Button>
                  </div>

                  <div className="grid sm:grid-cols-2 gap-3">
                    <Input
                      value={goalTitle}
                      onChange={(e) => setGoalTitle(e.target.value)}
                      placeholder="Goal title"
                      maxLength={100}
                      className="bg-stone-900 border-stone-700 text-white"
                      data-testid="family-goal-title"
                    />

                    <div>
                      <label className="text-stone-500 text-xs block mb-1">
                        Workouts per week
                      </label>
                      <Input
                        type="number"
                        min="1"
                        max="7"
                        step="1"
                        value={goalWeeklyTarget}
                        onChange={(e) =>
                          setGoalWeeklyTarget(Number(e.target.value))
                        }
                        className="bg-stone-900 border-stone-700 text-white"
                        data-testid="family-goal-weekly-target"
                      />
                    </div>
                  </div>

                  <Button
                    type="submit"
                    disabled={busy}
                    className="bg-blood-700 hover:bg-blood-800 text-white font-bold"
                    data-testid="family-goal-save"
                  >
                    Save Goal
                  </Button>
                </form>
              )}

              {familyGoals.length === 0 ? (
                <div className="rounded-lg border border-stone-800 bg-stone-900/40 p-4">
                  <p className="text-stone-400 text-sm">
                    No Family goals yet.
                  </p>
                  <p className="text-stone-600 text-xs mt-1">
                    {isOwner
                      ? 'Use Goal beside a household member to create one.'
                      : 'Set a weekly workout target when you are ready.'}
                  </p>
                </div>
              ) : (
                <div className="space-y-2">
                  {familyGoals.map((goal) => {
                    const canManage =
                      isOwner ||
                      goal.member_user_id === membership?.user_id;

                    const goalMember =
                      members.find(
                        (member) =>
                          member.user_id === goal.member_user_id
                      ) || {
                        user_id: goal.member_user_id,
                        username: goal.member_username,
                      };

                    return (
                      <div
                        key={goal.id}
                        className="rounded-lg border border-stone-800 bg-stone-900/50 p-4"
                        data-testid={`family-goal-${goal.id}`}
                      >
                        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                          <div className="min-w-0">
                            <div className="flex items-center gap-2 flex-wrap">
                              <p className="text-white font-semibold">
                                {goal.title || 'Weekly Workout Goal'}
                              </p>

                              <span className="text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full border border-stone-700 text-stone-400">
                                {goal.weekly_workout_target} / week
                              </span>
                            </div>

                            {isOwner && (
                              <p className="text-stone-500 text-xs mt-1">
                                {goal.member_username}
                              </p>
                            )}

                            {goal.progress?.shared ? (
                              <div className="mt-3">
                                <div className="flex items-center justify-between gap-3 text-xs">
                                  <span className="text-stone-400">
                                    {goal.progress.workouts_this_week ?? 0} of{' '}
                                    {goal.progress.weekly_workout_target ??
                                      goal.weekly_workout_target}{' '}
                                    workouts
                                  </span>

                                  <span className="text-stone-500">
                                    {goal.progress.remaining ?? 0} remaining
                                  </span>
                                </div>

                                <div className="h-2 rounded-full bg-stone-800 mt-2 overflow-hidden">
                                  <div
                                    className="h-full bg-blood-700 transition-all"
                                    style={{
                                      width: `${Math.max(
                                        0,
                                        Math.min(
                                          100,
                                          goal.progress.progress_pct ?? 0
                                        )
                                      )}%`,
                                    }}
                                  />
                                </div>
                              </div>
                            ) : (
                              <p
                                className="text-stone-600 text-sm mt-3"
                                data-testid={`family-goal-private-${goal.id}`}
                              >
                                Workout progress is private.
                              </p>
                            )}
                          </div>

                          {canManage && (
                            <div className="flex gap-2">
                              <Button
                                type="button"
                                variant="outline"
                                className="border-stone-700 text-stone-300"
                                onClick={() => beginFamilyGoal(goalMember)}
                                disabled={busy}
                                data-testid={`family-goal-edit-${goal.id}`}
                              >
                                Edit
                              </Button>

                              <Button
                                type="button"
                                variant="ghost"
                                className="text-stone-500 hover:text-red-400"
                                onClick={() =>
                                  deleteFamilyGoal(goal.member_user_id)
                                }
                                disabled={busy}
                                data-testid={`family-goal-delete-${goal.id}`}
                              >
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            </div>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </Card>

            <Card
              className="glass border-stone-800 p-6 mb-5"
              data-testid="family-workout-recommendations-card"
            >
              <div className="flex items-center gap-2 mb-1">
                <Dumbbell className="w-5 h-5 text-blood-400" />
                <h2 className="text-lg text-white font-bold">
                  Workout Recommendations
                </h2>
              </div>

              <p className="text-stone-500 text-xs mb-4">
                {isOwner
                  ? 'Recommend workouts to household members without replacing their personal workout plan.'
                  : 'Your Family Owner can recommend workouts. You decide whether to accept or dismiss each one.'}
              </p>

              {isOwner && recommendationTarget && (
                <form
                  onSubmit={submitWorkoutRecommendation}
                  className="rounded-lg border border-blood-900/60 bg-blood-950/20 p-4 mb-4 space-y-3"
                  data-testid="family-workout-recommendation-form"
                >
                  <div className="flex items-center justify-between gap-3">
                    <div>
                      <p className="text-xs uppercase tracking-wider text-blood-400">
                        Recommend to
                      </p>
                      <p className="text-white font-semibold">
                        {recommendationTarget.username}
                      </p>
                    </div>

                    <Button
                      type="button"
                      variant="ghost"
                      className="text-stone-500"
                      onClick={cancelWorkoutRecommendation}
                      disabled={busy}
                    >
                      Cancel
                    </Button>
                  </div>

                  <div className="grid sm:grid-cols-2 gap-3">
                    <Input
                      value={recommendationTitle}
                      onChange={(e) =>
                        setRecommendationTitle(e.target.value)
                      }
                      placeholder="Workout title"
                      maxLength={100}
                      className="bg-stone-900 border-stone-700 text-white"
                      data-testid="family-recommendation-title"
                    />

                    <select
                      value={recommendationWorkoutType}
                      onChange={(e) =>
                        setRecommendationWorkoutType(e.target.value)
                      }
                      className="h-10 rounded-md border border-stone-700 bg-stone-900 px-3 text-sm text-white"
                      data-testid="family-recommendation-workout-type"
                    >
                      <option value="home">Home</option>
                      <option value="gym">Gym</option>
                      <option value="bodyweight">Bodyweight</option>
                      <option value="yoga_pilates">Yoga / Pilates</option>
                    </select>

                    <Input
                      value={recommendationFocus}
                      onChange={(e) =>
                        setRecommendationFocus(e.target.value)
                      }
                      placeholder="Focus, e.g. chest_triceps"
                      maxLength={80}
                      className="bg-stone-900 border-stone-700 text-white"
                      data-testid="family-recommendation-focus"
                    />

                    <Input
                      type="date"
                      value={recommendationDate}
                      onChange={(e) =>
                        setRecommendationDate(e.target.value)
                      }
                      className="bg-stone-900 border-stone-700 text-white"
                      data-testid="family-recommendation-date"
                    />
                  </div>

                  <textarea
                    value={recommendationNotes}
                    onChange={(e) =>
                      setRecommendationNotes(e.target.value)
                    }
                    placeholder="Optional note"
                    maxLength={1000}
                    rows={3}
                    className="w-full rounded-md border border-stone-700 bg-stone-900 px-3 py-2 text-sm text-white placeholder:text-stone-500"
                    data-testid="family-recommendation-notes"
                  />

                  <Button
                    type="submit"
                    disabled={busy}
                    className="bg-blood-700 hover:bg-blood-800 text-white font-bold"
                    data-testid="family-recommendation-submit"
                  >
                    Recommend Workout
                  </Button>
                </form>
              )}

              {familyRecommendations.length === 0 ? (
                <div className="rounded-lg border border-stone-800 bg-stone-900/40 p-4">
                  <p className="text-stone-400 text-sm">
                    No workout recommendations yet.
                  </p>
                  {isOwner && (
                    <p className="text-stone-600 text-xs mt-1">
                      Use Recommend beside a household member to create one.
                    </p>
                  )}
                </div>
              ) : (
                <div className="space-y-2">
                  {familyRecommendations.map((recommendation) => {
                    const isMine =
                      recommendation.member_user_id ===
                      membership?.user_id;

                    return (
                      <div
                        key={recommendation.id}
                        className="rounded-lg border border-stone-800 bg-stone-900/50 p-4"
                        data-testid={`family-workout-recommendation-${recommendation.id}`}
                      >
                        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                          <div>
                            <div className="flex items-center gap-2 flex-wrap">
                              <p className="text-white font-semibold">
                                {recommendation.title}
                              </p>

                              <span
                                className={`text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full border ${
                                  recommendation.status === 'accepted'
                                    ? 'border-green-800 text-green-400'
                                    : recommendation.status === 'dismissed'
                                      ? 'border-stone-700 text-stone-500'
                                      : 'border-amber-800 text-amber-400'
                                }`}
                              >
                                {recommendation.status}
                              </span>
                            </div>

                            <p className="text-stone-500 text-xs mt-1">
                              {isOwner
                                ? `For ${recommendation.member_username}`
                                : `From ${recommendation.created_by_username}`}
                              {' | '}
                              {recommendation.workout_type}
                              {' | '}
                              {recommendation.focus}
                              {recommendation.scheduled_date
                                ? ` | ${recommendation.scheduled_date}`
                                : ''}
                            </p>

                            {recommendation.notes && (
                              <p className="text-stone-400 text-sm mt-2">
                                {recommendation.notes}
                              </p>
                            )}
                          </div>

                          {isMine &&
                            recommendation.status === 'recommended' && (
                              <div className="flex gap-2">
                                <Button
                                  type="button"
                                  onClick={() =>
                                    respondToWorkoutRecommendation(
                                      recommendation.id,
                                      'accept'
                                    )
                                  }
                                  disabled={busy}
                                  className="bg-blood-700 hover:bg-blood-800 text-white"
                                  data-testid={`family-recommendation-accept-${recommendation.id}`}
                                >
                                  Accept
                                </Button>

                                <Button
                                  type="button"
                                  variant="outline"
                                  onClick={() =>
                                    respondToWorkoutRecommendation(
                                      recommendation.id,
                                      'dismiss'
                                    )
                                  }
                                  disabled={busy}
                                  className="border-stone-700 text-stone-400"
                                  data-testid={`family-recommendation-dismiss-${recommendation.id}`}
                                >
                                  Dismiss
                                </Button>
                              </div>
                            )}
                          {isMine &&
                            recommendation.status === 'accepted' && (
                              <Button
                                type="button"
                                onClick={() =>
                                  startWorkoutRecommendation(
                                    recommendation
                                  )
                                }
                                disabled={busy}
                                className="bg-blood-700 hover:bg-blood-800 text-white"
                                data-testid={`family-recommendation-start-${recommendation.id}`}
                              >
                                <Dumbbell className="w-4 h-4 mr-2" />
                                Start Workout
                              </Button>
                            )}

                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </Card>

            <Card
              className="glass border-stone-800 p-6 mb-5"
              data-testid="family-challenges-card"
            >
              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-2">
                <div className="flex items-center gap-2">
                  <Swords className="w-5 h-5 text-blood-400" />
                  <h2 className="text-lg text-white font-bold">
                    Family Challenges
                  </h2>
                </div>

                <div className="flex flex-wrap gap-2">
                  {familyGroupCandidates.length >= 2 && (
                    <Button
                      type="button"
                      className="bg-blood-700 hover:bg-blood-800 text-white"
                      onClick={() =>
                        navigate('/battle-arena', {
                          state: {
                            challengeMode: 'group',
                            challengeSource: 'family',
                            challengeMembers: familyGroupCandidates,
                          },
                        })
                      }
                      data-testid="family-group-challenge"
                    >
                      <Swords className="w-4 h-4 mr-2" />
                      Create Group Challenge
                    </Button>
                  )}

                  <Button
                    type="button"
                    variant="outline"
                    className="border-blood-800 text-blood-400"
                    onClick={() => navigate('/battle-arena')}
                    data-testid="family-open-battle-arena"
                  >
                    Open Battle Arena
                  </Button>
                </div>
              </div>

              <p className="text-stone-500 text-xs mb-4">
                Household challenges use the normal Battle Arena rules,
                scoring, limits, and premium requirements.
              </p>

              {familyChallenges.length === 0 ? (
                <div className="rounded-lg border border-stone-800 bg-stone-900/40 p-4">
                  <p className="text-stone-400 text-sm">
                    No pending or active Family challenges.
                  </p>

                  <p className="text-stone-600 text-xs mt-1">
                    {familyGroupCandidates.length >= 2
                      ? 'Challenge one household member or create a group Battle for the Family.'
                      : 'Use the Challenge button beside a household member to start one.'}
                  </p>
                </div>
              ) : (
                <div className="space-y-2">
                  {familyChallenges.map((battle) => {
                    if (battle.format === 'group') {
                      const participants = Array.isArray(
                        battle.participants
                      )
                        ? battle.participants
                        : [];

                      const acceptedCount = participants.filter(
                        (participant) =>
                          participant.invite_status === 'accepted'
                      ).length;

                      const invitedCount = participants.filter(
                        (participant) =>
                          participant.invite_status === 'invited'
                      ).length;

                      const participantCount =
                        battle.participant_count ||
                        participants.length;

                      return (
                        <div
                          key={battle.id}
                          className="rounded-lg border border-stone-800 bg-stone-900/50 p-4 flex flex-col sm:flex-row sm:items-center justify-between gap-3"
                          data-testid={`family-battle-${battle.id}`}
                        >
                          <div>
                            <div className="flex items-center gap-2 flex-wrap">
                              <p className="text-white font-semibold">
                                Group Battle
                              </p>

                              <span
                                className={`text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full border ${
                                  battle.status === 'active'
                                    ? 'border-green-800 text-green-400'
                                    : 'border-amber-800 text-amber-400'
                                }`}
                              >
                                {battle.status}
                              </span>
                            </div>

                            <p className="text-stone-500 text-xs mt-1">
                              {participantCount} participants
                              {' | '}
                              {battle.battle_type || 'challenge'}
                              {' | '}
                              {battle.battle_category || 'workout'}
                            </p>

                            {battle.status === 'pending' && (
                              <p className="text-stone-600 text-xs mt-1">
                                {acceptedCount} accepted
                                {invitedCount > 0
                                  ? ` | ${invitedCount} waiting`
                                  : ''}
                              </p>
                            )}
                          </div>

                          <Button
                            type="button"
                            variant="ghost"
                            className="text-blood-400 hover:text-blood-300"
                            onClick={() => navigate('/battle-arena')}
                          >
                            View Battle
                          </Button>
                        </div>
                      );
                    }

                    const sentByMe =
                      battle.challenger_id ===
                      membership?.user_id;

                    const otherUsername = sentByMe
                      ? battle.opponent_username
                      : battle.challenger_username;

                    const relationshipText =
                      battle.status === 'active'
                        ? `Active with ${otherUsername}`
                        : sentByMe
                          ? `Waiting for ${otherUsername}`
                          : `Challenge from ${otherUsername}`;

                    return (
                      <div
                        key={battle.id}
                        className="rounded-lg border border-stone-800 bg-stone-900/50 p-4 flex flex-col sm:flex-row sm:items-center justify-between gap-3"
                        data-testid={`family-battle-${battle.id}`}
                      >
                        <div>
                          <div className="flex items-center gap-2">
                            <p className="text-white font-semibold">
                              {relationshipText}
                            </p>

                            <span
                              className={`text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full border ${
                                battle.status === 'active'
                                  ? 'border-green-800 text-green-400'
                                  : 'border-amber-800 text-amber-400'
                              }`}
                            >
                              {battle.status}
                            </span>
                          </div>

                          <p className="text-stone-500 text-xs mt-1">
                            {battle.battle_type || 'challenge'}
                            {' | '}
                            {battle.battle_category || 'workout'}
                          </p>
                        </div>

                        <Button
                          type="button"
                          variant="ghost"
                          className="text-blood-400 hover:text-blood-300"
                          onClick={() => navigate('/battle-arena')}
                        >
                          View Battle
                        </Button>
                      </div>
                    );
                  })}
                </div>
              )}
            </Card>

            <Card className="glass border-stone-800 p-6" data-testid="family-privacy-card">
              <div className="flex items-center gap-2 mb-2"><Shield className="w-5 h-5 text-blood-400" /><h2 className="text-lg text-white font-bold">My Family Sharing</h2></div>
              <p className="text-stone-400 text-sm mb-5">
  {isOwner
    ? 'Your personal fitness, nutrition, weight, and photo data starts private. Choose what you want included in Family features.'
    : 'Your fitness, nutrition, weight, and photo data starts private. Choose what the Family Owner may see.'}
</p>
              <div className="space-y-3">{PRIVACY_OPTIONS.map(([key, label, description]) => <div key={key} className="flex items-center justify-between gap-4 border border-stone-800 rounded-lg p-3"><div><p className="text-white text-sm font-semibold">{label}</p><p className="text-stone-500 text-xs">{description}</p></div><button type="button" onClick={() => setPrivacy((prev) => ({ ...prev, [key]: !prev[key] }))} className={`w-12 h-7 rounded-full transition-all ${privacy[key] ? 'bg-blood-700' : 'bg-stone-700'}`} data-testid={`family-privacy-${key}`}><span className={`block w-5 h-5 bg-white rounded-full transition-transform ${privacy[key] ? 'translate-x-6' : 'translate-x-1'}`} /></button></div>)}</div>
              <Button onClick={savePrivacy} disabled={busy} className="w-full mt-5 bg-blood-700 hover:bg-blood-800 text-white font-bold">Save Sharing Preferences</Button>
            </Card>
          </>
        )}
      </div>
    </div>
  );
};

export default FamilyHub;
